import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Lock, UserPlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminLoginPage() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      toast.error("Заполните все поля");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });

      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_username", response.data.username);
      localStorage.setItem("admin_role", response.data.role || "admin");
      toast.success("Вход выполнен");
      
      // Redirect based on role
      if (response.data.role === "operator") {
        navigate("/operator/dashboard");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Неверные данные");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!username || !password || !inviteCode) {
      toast.error("Заполните все поля");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Пароли не совпадают");
      return;
    }

    if (password.length < 6) {
      toast.error("Пароль должен быть минимум 6 символов");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/register`, {
        username,
        password,
        invite_code: inviteCode
      });

      toast.success("Регистрация успешна! Теперь войдите");
      setIsRegister(false);
      setPassword("");
      setConfirmPassword("");
      setInviteCode("");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Ошибка регистрации");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center gap-4">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            size="icon"
            className="hover:bg-primary/10 hover:text-primary"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </Button>
          <Link to="/" className="font-heading text-2xl font-bold text-primary tracking-tight hover:text-primary/80 transition-colors">
            SKINKOSH
          </Link>
        </div>
      </header>

      {/* Login/Register Form */}
      <div className="flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full bg-card border border-border p-8 rounded-lg" data-testid="admin-login-form">
          <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 flex items-center justify-center rounded-lg">
            {isRegister ? (
              <UserPlus className="text-primary" size={32} />
            ) : (
              <Lock className="text-primary" size={32} />
            )}
          </div>
          
          <h2 className="font-heading text-2xl font-bold mb-6 text-center text-primary uppercase tracking-wide">
            {isRegister ? "РЕГИСТРАЦИЯ" : "АДМИН-ПАНЕЛЬ"}
          </h2>

          <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-muted-foreground mb-2 block">
                Имя пользователя
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                required
                className="h-12 bg-input border-border focus:border-primary"
                data-testid="username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-muted-foreground mb-2 block">
                Пароль
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                required
                className="h-12 bg-input border-border focus:border-primary"
                data-testid="password-input"
              />
            </div>

            {isRegister && (
              <>
                <div>
                  <Label htmlFor="confirmPassword" className="text-muted-foreground mb-2 block">
                    Подтвердите пароль
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••"
                    required
                    className="h-12 bg-input border-border focus:border-primary"
                  />
                </div>

                <div>
                  <Label htmlFor="inviteCode" className="text-muted-foreground mb-2 block">
                    Код приглашения
                  </Label>
                  <Input
                    id="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="XXXXXX"
                    required
                    className="h-12 bg-input border-border focus:border-primary"
                  />
                </div>
              </>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-lg mt-6"
              data-testid="login-button"
            >
              {loading ? "ЗАГРУЗКА..." : isRegister ? "ЗАРЕГИСТРИРОВАТЬСЯ" : "ВОЙТИ"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                setPassword("");
                setConfirmPassword("");
                setInviteCode("");
              }}
              className="text-primary hover:text-primary/80 text-sm underline"
            >
              {isRegister ? "Уже есть аккаунт? Войти" : "Нет аккаунта? Регистрация"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLoginPage;
